import React, {FC, ReactNode} from 'react';
import {connect} from 'react-redux';

import {VirtualTableHandle} from '../../../../../../../../src/components/common/table/virtualTable';
import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {AccountModel} from '../../../../../../../../src/models/api/accountModel';
import {AppState} from '../../../../../../../../src/store/appState';
import {
  getContactsManagerTableState,
  makeGetAccountContactsTableCardIds,
  makeGetContactsManagerTableOffsetPaginationData,
  makeGetContactsManagerTableState,
  makeGetIsInMultiSelectMode,
  makeGetRouteCardId,
  makeGetSelectedCardIds,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {
  contactsManagerFetchAccountContactsTableDataAsyncAction,
  contactsManagerSetUpAccountContactsTableAction,
  contactsManagerTableAddSelectedCardIdsAction,
  contactsManagerTableCleanupAction,
  contactsManagerTableRemoveSelectedCardIdsAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {tableStateIsAccountContactsTableState} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncHandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../../shared/helpers/src/utilities/asyncCancelHelpers';
import {makeGetDynamicColumns} from '../common/tableSelectors';
import {AccountContactsTable} from './accountContactsTable';

/*
 * Props.
 */

interface AccountContactsTableContainerPublicProps {
  panelId: ContactsManagerPanelId;
  account: AccountModel;
  tableRef: React.MutableRefObject<VirtualTableHandle | undefined>;
  multiSelectHeaderActions: ReactNode;
  addCardToAccount: AsyncHandlerOf<number>;
}

type AccountContactsTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountContactsTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountContactsTableContainerComponentProps = AccountContactsTableContainerPublicProps &
  AccountContactsTableContainerStateProps &
  AccountContactsTableContainerDispatchProps;

/*
 * Selectors.
 */

function getAccount(_state: AppState, props: AccountContactsTableContainerPublicProps) {
  return props.account;
}

function makeGetHasDomainsConnected() {
  return createSelector([getAccount], (account) => (account.domains?.length ?? 0) > 0);
}

function makeGetAccountContactsTableStatus() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsAccountContactsTableState(tableState)) {
      return undefined;
    }
    return tableState.status;
  });
}

/*
 * Component.
 */

export const AccountContactsTableContainerComponent: FC<AccountContactsTableContainerComponentProps> = (
  props,
) => {
  const {
    panelId,
    account,
    dynamicColumns,
    hasDomainsConnected,
    tableRef,
    cardIds,
    paginationData,
    routeCardId,
    multiSelectHeaderActions,
    selectedCardIds,
    isInMultiSelectMode,
    cleanup,
    setUpAccountContactsTable,
    addCardToAccount,
    onFetchTableDataAsync,
    onAddSelectedCardIds,
    onRemoveSelectedCardIds,
    tableStatus,
  } = props;

  useInitialEffect(() => {
    setUpAccountContactsTable(account.id, routeCardId);

    // Listen for the component to be unmounted and clean it up.
    return () => {
      cleanup();
    };
  });

  const onReset = () => {
    cleanup();
    setUpAccountContactsTable(account.id, routeCardId);
  };

  // The table can't fetch until it's set up in Redux.
  if (tableStatus === undefined) {
    return null;
  }

  return (
    <AccountContactsTable
      panelId={panelId}
      account={account}
      dynamicColumns={dynamicColumns}
      hasDomainsConnected={hasDomainsConnected}
      tableRef={tableRef}
      cardIds={cardIds}
      paginationData={paginationData}
      multiSelectHeaderActions={multiSelectHeaderActions}
      selectedCardIds={selectedCardIds}
      isInMultiSelectMode={isInMultiSelectMode}
      onFetchTableDataAsync={onFetchTableDataAsync}
      onReset={onReset}
      addCardToAccount={addCardToAccount}
      onAddSelectedCardIds={onAddSelectedCardIds}
      onRemoveSelectedCardIds={onRemoveSelectedCardIds}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountContactsTableContainerPublicProps>()(() => {
  const getTableState = makeGetContactsManagerTableState();
  return {
    cardIds: makeGetAccountContactsTableCardIds(),
    dynamicColumns: makeGetDynamicColumns(),
    hasDomainsConnected: makeGetHasDomainsConnected(),
    paginationData: makeGetContactsManagerTableOffsetPaginationData(getTableState),
    routeCardId: makeGetRouteCardId(),
    isInMultiSelectMode: makeGetIsInMultiSelectMode(),
    selectedCardIds: makeGetSelectedCardIds(),
    tableStatus: makeGetAccountContactsTableStatus(),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<AccountContactsTableContainerPublicProps>()(
  ({panelId, account}) => ({
    onAddSelectedCardIds: contactsManagerTableAddSelectedCardIdsAction,
    onRemoveSelectedCardIds: contactsManagerTableRemoveSelectedCardIdsAction,
    setUpAccountContactsTable: contactsManagerSetUpAccountContactsTableAction,
    cleanup: contactsManagerTableCleanupAction,
    onFetchTableDataAsync: (cancelToken: CancelToken) =>
      contactsManagerFetchAccountContactsTableDataAsyncAction(panelId, account.id, cancelToken),
  }),
);

export const AccountContactsTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountContactsTableContainerComponent);
